import { useEffect, useMemo, useCallback } from 'react';
import {
  Stack,
  Group,
  Button,
  Select,
  Text,
  Paper,
  Table,
  Alert,
  Radio,
} from '@mantine/core';
import { IconArrowLeft, IconAlertTriangle } from '@tabler/icons-react';
import type { ImportWizardState, TargetField } from '../types';
import {
  detectColumnMappings,
  getAvailableTargets,
} from '../utils/columnDetection';
import { detectAmbiguousDates } from '../utils/dateDetection';

interface ColumnMappingStepProps {
  state: ImportWizardState;
  onUpdate: (updates: Partial<ImportWizardState>) => void;
  onNext: () => void;
  onBack: () => void;
}

export function ColumnMappingStep({
  state,
  onUpdate,
  onNext,
  onBack,
}: ColumnMappingStepProps) {
  const { parsedData, columnMappings, dateFormat } = state;

  // Auto-detect column mappings on mount or when parsedData changes
  useEffect(() => {
    if (parsedData && columnMappings.length === 0) {
      const detected = detectColumnMappings(parsedData.headers, parsedData.rows);
      onUpdate({ columnMappings: detected });
    }
  }, [parsedData, columnMappings.length, onUpdate]);

  // Check for ambiguous dates
  const ambiguousDates = useMemo(() => {
    if (!parsedData || columnMappings.length === 0) return null;
    return detectAmbiguousDates(parsedData.rows, columnMappings);
  }, [parsedData, columnMappings]);

  // Handle mapping change for a column
  const handleMappingChange = useCallback(
    (index: number, value: string | null) => {
      if (!value) return;
      const updated = columnMappings.map((m, i) =>
        i === index ? { ...m, targetField: value as TargetField } : m,
      );
      onUpdate({ columnMappings: updated });
    },
    [columnMappings, onUpdate],
  );

  // Handle date format selection
  const handleDateFormatChange = useCallback(
    (value: string) => {
      onUpdate({ dateFormat: value as 'US' | 'EU' });
    },
    [onUpdate],
  );

  // Validation: required fields must be mapped
  const hasName = columnMappings.some((m) => m.targetField === 'name');
  const hasDate = columnMappings.some(
    (m) => m.targetField === 'acquisition_date',
  );
  const needsDateFormat = ambiguousDates !== null && dateFormat === null;
  const canProceed = hasName && hasDate && !needsDateFormat;

  // Build missing fields message
  const missingFields: string[] = [];
  if (!hasName) missingFields.push('Name');
  if (!hasDate) missingFields.push('Acquisition Date');

  if (!parsedData || columnMappings.length === 0) {
    return (
      <Stack gap="lg" mt="lg">
        <Text c="dimmed" ta="center" py="xl">
          Loading column mappings...
        </Text>
      </Stack>
    );
  }

  return (
    <Stack gap="lg" mt="lg">
      <Text size="sm" c="dimmed">
        Map your spreadsheet columns to the appropriate fields. Columns matched
        automatically are pre-selected -- adjust as needed.
      </Text>

      {/* Column mapping table */}
      <Paper withBorder>
        <Table highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Your Column</Table.Th>
              <Table.Th>Maps To</Table.Th>
              <Table.Th>Sample Values</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {columnMappings.map((mapping, index) => {
              const options = getAvailableTargets(columnMappings, index);
              return (
                <Table.Tr key={mapping.sourceColumn}>
                  <Table.Td>
                    <Text fw={500} size="sm">
                      {mapping.sourceColumn}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Select
                      data={options}
                      value={mapping.targetField}
                      onChange={(value) => handleMappingChange(index, value)}
                      size="sm"
                      allowDeselect={false}
                      style={{ minWidth: 200 }}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed" lineClamp={1}>
                      {mapping.sampleValues.length > 0
                        ? mapping.sampleValues.join(', ')
                        : '--'}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Paper>

      {/* Ambiguous date format prompt */}
      {ambiguousDates && hasDate && (
        <Alert
          icon={<IconAlertTriangle size={18} />}
          title="Ambiguous date format detected"
          color="yellow"
        >
          <Stack gap="sm">
            <Text size="sm">
              The date <Text span fw={600}>"{ambiguousDates.ambiguousExample}"</Text> could
              be read two ways. Which format does your spreadsheet use?
            </Text>
            <Radio.Group value={dateFormat ?? ''} onChange={handleDateFormatChange}>
              <Stack gap="xs">
                <Radio
                  value="US"
                  label={`Month/Day/Year (${ambiguousDates.possibleUS})`}
                />
                <Radio
                  value="EU"
                  label={`Day/Month/Year (${ambiguousDates.possibleEU})`}
                />
              </Stack>
            </Radio.Group>
          </Stack>
        </Alert>
      )}

      {/* Validation message */}
      {missingFields.length > 0 && (
        <Alert color="red" variant="light">
          <Text size="sm">
            Required fields not mapped:{' '}
            <Text span fw={600}>
              {missingFields.join(', ')}
            </Text>
            . Please assign a column to each required field before continuing.
          </Text>
        </Alert>
      )}

      {/* Navigation buttons */}
      <Group justify="space-between" mt="md">
        <Button
          variant="default"
          onClick={onBack}
          leftSection={<IconArrowLeft size={16} />}
        >
          Back
        </Button>
        <Button onClick={onNext} disabled={!canProceed} size="md">
          Next: Review
        </Button>
      </Group>
    </Stack>
  );
}
